import React, {Component} from 'react';
import {FormattedMessage, FormattedRelative} from 'react-intl';
import {PropTypes} from 'prop-types';
import {connect} from 'react-redux';

import {Button, Modal, Alert as StandardAlert, Icon, ExclamationIcon} from 'eui-components';
import {api} from '../api';

/**
 * Component that will warn a user when their session is about to expire.  The user will then have the option to
 * refresh their session or logout.
 */
export class AutoLogout extends Component {

  /**
   * Five minutes, in milliseconds.
   *
   * @type {number}
   */
  static WARNING_DURATION_MILLISECONDS = (5 * 60 * 1000);
  // static WARNING_DURATION_MILLISECONDS = (5 * 1000);

  /**
   * Sessions last two hours.
   *
   * @type {number}
   */
  static SESSION_TIMEOUT_MILLISECONDS = 7200 * 1000;
  // static SESSION_TIMEOUT_MILLISECONDS = 15 * 1000;

  static propTypes = {
    debug: PropTypes.bool.isRequired,
    sessionTimeout: PropTypes.number.isRequired,
    warningDuration: PropTypes.number.isRequired,
    logoutUrl: PropTypes.string.isRequired,
    onRefreshSession: PropTypes.func.isRequired
  };

  static defaultProps = {
    debug: false,
    sessionTimeout: AutoLogout.SESSION_TIMEOUT_MILLISECONDS,
    warningDuration: AutoLogout.WARNING_DURATION_MILLISECONDS,
    logoutUrl: `${process.env.PUBLIC_URL}/logout`,
    onRefreshSession: () => {}
  };

  constructor(props) {
    // console.debug('AutoLogout constructor');
    super(props);

    this.state = {
      showWarning: false
    };

    this.events = [
      'load',
      'mousemove',
      'mousedown',
      'click',
      'scroll',
      'keypress'
    ];
  }

  componentDidMount() {
    this.debug('componentDidMount');

    this.events.forEach((eventName) => {
      window.addEventListener(eventName, this.resetTimeout);
    });
    this.setTimeout();
  }

  componentWillUnmount() {
    this.debug('componentWillUnmount');
    this.events.forEach((eventName) => {
      window.removeEventListener(eventName, this.resetTimeout);
    });
    this.clearTimeoutFunc();
  }

  static getDerivedStateFromProps(props, state)  {
    const nextState =  {
      warningTime: props.sessionTimeout - props.warningDuration,
      signoutTime: props.sessionTimeout
    };

    if (state.warningTime === nextState.warningTime && state.signoutTime === nextState.signoutTime ) {
      return null;
    } else {
      return nextState;
    }
  }

  /**
   * Clears all timeout functions used by this component.
   */
  clearTimeoutFunc = () => {
    if (this.warnTimeout) {
      clearTimeout(this.warnTimeout);
    }

    if (this.logoutTimeout) {
      clearTimeout(this.logoutTimeout);
    }
  };

  setTimeout = () => {
    this.warnTimeout = setTimeout(this.warn, this.state.warningTime);
    this.logoutTimeout = setTimeout(this.logout, this.state.signoutTime);

    if (this.props.debug) {
      this.debug('Next warning at ' + new Date(Date.now() + this.state.warningTime) +
          ' logout at: ' + new Date(Date.now() + this.state.signoutTime));
    }
  };

  resetTimeout = (event) => {
    if (this.state.showWarning) {
      this.debug('Skipping session timer reset as warning modal is displayed. Only clicking \'Continue Session\' can extend the session now.');
    } else {
      if (event) {
        this.debug(`Resetting session timeouts due to '${event.type}' event `);
      }

      this.clearTimeoutFunc();
      this.setTimeout();
    }
  };

  warn = () => {
    this.setState({showWarning: true});
  };

  /**
   * Redirects the user to the logout URL, ending their session.
   */
  logout = () => {
    window.location.assign(this.props.logoutUrl);
  };

  debug = (m) => {
    if (this.props.debug) {
      console.log(m);
    }
  }

  /**
   * Refresh the user's session by calling an API endpoint in the background.  Reset session timeouts.
   */
  refresh = () => {
    this.props.onRefreshSession();
    this.setState({showWarning: false});
    this.resetTimeout();
  };

  render() {
    if (this.state.showWarning) {
      return this.renderSessionTimeoutWarning();
    }
    return null;
  }

  /**
   * Renders a modal dialog, prompting the user to either continue their session or to immediately logout.
   *
   * @return {*} A JSX representation of a session timeout warning.
   */
  renderSessionTimeoutWarning = () => {
    const continueMessage = <FormattedMessage id='AutoLogout.continueBtn' defaultMessage='Continue Session'/>;
    const signOutButton = <FormattedMessage id='AutoLogout.logoutBtn' defaultMessage='Sign Out'/>;
    const date = Date.now() + this.props.warningDuration;

    this.debug('Counting down to ' +  new Date(date));

    return (
        <div className="auto-logout-modal">
          <Modal show={this.state.showWarning}>
            <Modal.Header>
              <Modal.Title>
                <FormattedMessage id='AutoLogout.expiringSessionTitle' defaultMessage='Session Timeout Warning'/>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <StandardAlert bsStyle="warning">
                <Icon icon={ExclamationIcon}/>
                <div className='alert-content'>
                <FormattedMessage tagName='p' id='AutoLogout.expiringSessionBody'
                                  defaultMessage='Your session will expire {when}.'
                                  values={{
                                    when: <FormattedRelative value={date} updateInterval={1000}>
                                      {(formattedRelative) => (
                                          <b>{formattedRelative}</b>
                                      )}
                                    </FormattedRelative>

                                  }}/>
                <FormattedMessage tagName='p'
                                  id='AutoLogout.expiringSessionOptions'
                                  defaultMessage='Click {continueButton} to keep working or click {signOutButton} to end your session.'
                                  values={{
                                    continueButton: <b>{continueMessage}</b>,
                                    signOutButton: <b>{signOutButton}</b>
                                  }}/>
                </div>
              </StandardAlert>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={this.refresh} bsStyle="primary">{continueMessage}</Button>
              <Button onClick={this.logout}>{signOutButton}</Button>
            </Modal.Footer>
          </Modal>
        </div>
    );
  };
}

/**
 * Map actions to component properties.
 *
 * @param dispatch
 * @returns {{changeContextInstitution: changeContextInstitution|ActionCreator<any>|ActionCreatorsMapObject}}
 */
const mapDispatchToProps = dispatch => ({
  onRefreshSession: () => {
    api('/api/state').then(
        response => response.json(),
        error => console.log('An error occurred.', error)
    );
  }
});

export default connect(null, mapDispatchToProps)(AutoLogout);



// WEBPACK FOOTER //
// ./src/AutoLogout/AutoLogout.js